import api from './api';

// Talks to /api/secondary/* — secondary distribution, depot to customer and
// RDC to own store. Version and site come from the x-budget-version-id /
// x-site-code headers (set by the api interceptor), so nothing here passes
// them explicitly.
//
// Site scoping follows inter-depot: a zone is owned by its ORIGIN, the depot
// that dispatches is the one that pays. Blank = All Sites.
export const secondaryService = {
  // ---- Route to Market › Secondary Lanes & Rates ---------------------------
  // { zones, assignments, storeSchedules, lanes, carriers, rates, picks, config }
  // Store rows are absent — not hidden — when store replenishment is off.
  getSetup: async () => {
    const res = await api.get('/secondary/setup');
    return res.data;
  },

  // ---- The two budget pages ------------------------------------------------
  // kind: 'CUSTOMER' | 'STORE'. Same shape either way — the kind only decides
  // which lanes come back, which is why both pages share one component.
  // { lanes: [{ ...lane, drivers, manual, carriers: [{ ...c, drivers, rates }] }] }
  getBudget: async (kind) => {
    const res = await api.get('/secondary/budget', { params: { kind } });
    return res.data;
  },

  // grain: 'zone' | 'customer' | 'channel'. A rollup of one stored answer,
  // never a different calculation.
  getCostToServe: async (kind, grain = 'zone') => {
    const res = await api.get('/secondary/cost-to-serve', { params: { kind, grain } });
    return res.data;
  },

  // Cost per case by SKU for one customer or store. A function call, not a
  // table: cost per case is cost per load unit ÷ cases per load unit, so
  // storing it would be arithmetic against the item master.
  getCostToServeSku: async ({ member, zone, stream }) => {
    const res = await api.get('/secondary/cost-to-serve/sku', { params: { member, zone, stream } });
    return res.data;
  },

  // Median order size per customer and zone, from the forecast. The MEDIAN,
  // not the mean — a few full-truck orders drag the average up, which
  // understates drops and quietly understates the budget.
  getOrderSizes: async () => {
    const res = await api.get('/secondary/order-sizes');
    return res.data;
  },

  // ---- Zones (own page; master data, admin to write) -----------------------
  // Server-filtered top 100, same contract as the volume grids:
  // opts = { filters, limit, offset, scope } → { data, total, hasMore, limit }
  getZones: async (opts = {}) => {
    const params = {};
    if (opts.filters && Object.keys(opts.filters).length) params.filters = JSON.stringify(opts.filters);
    if (opts.limit != null) params.limit = opts.limit;
    if (opts.offset) params.offset = opts.offset;
    if (opts.scope) params.scope = opts.scope;
    const res = await api.get('/secondary/zones', { params });
    return res.data;
  },

  // BULK — the page has a Save button, not per-cell autosave. One transaction,
  // because a half-applied grid is worse than a rejected one.
  saveZones: async (zones) => {
    const res = await api.put('/secondary/zones', { zones });
    return res.data;
  },

  deleteZone: async (zoneCode) => {
    const res = await api.delete(`/secondary/zones/${encodeURIComponent(zoneCode)}`);
    return res.data;
  },

  // ---- Customer → zone mapping (master data, admin to write) ---------------
  // ONE row per customer: a ship-to that needs its own zone is its own customer
  // in the master. Two fields — which zone, and how big a drop is.
  //
  // Returns EVERY active customer, mapped or not: an unmapped customer is the
  // thing you most need to see.
  getAssignments: async (opts = {}) => {
    const params = {};
    if (opts.filters && Object.keys(opts.filters).length) params.filters = JSON.stringify(opts.filters);
    if (opts.limit != null) params.limit = opts.limit;
    if (opts.offset) params.offset = opts.offset;
    if (opts.scope) params.scope = opts.scope;
    const res = await api.get('/secondary/assignments', { params });
    return res.data;
  },

  // BULK. A blank zone_code UNMAPS that customer — clearing the cell is how you
  // say "this one is not delivered".
  saveAssignments: async (assignments) => {
    const res = await api.put('/secondary/assignments', { assignments });
    return res.data;
  },

  deleteAssignment: async (assignmentId) => {
    const res = await api.delete(`/secondary/assignments/${assignmentId}`);
    return res.data;
  },

  // ---- Store delivery schedule (master data, admin to write) ---------------
  // cluster × stream × format, most specific wins. Store COUNT is derived from
  // md_sites.cluster_code, never typed.
  // payload: { cluster_code, stream, store_format?, deliveries_pm, notes? }
  saveStoreSchedule: async (payload) => {
    const res = await api.put('/secondary/store-schedule', payload);
    return res.data;
  },

  // BULK, for the Save button on the store schedule grid.
  saveStoreSchedules: async (schedules) => {
    const res = await api.put('/secondary/store-schedule/bulk', { schedules });
    return res.data;
  },

  deleteStoreSchedule: async (scheduleId) => {
    const res = await api.delete(`/secondary/store-schedule/${scheduleId}`);
    return res.data;
  },

  // ---- Lanes ---------------------------------------------------------------
  // payload: { zone_code, stream?, drops_per_run?, volume_source?, notes? }
  // A store lane needs a stream; a customer lane must not have one.
  addLane: async (payload) => {
    const res = await api.post('/secondary/lanes', payload);
    return res.data;
  },

  // payload: { drops_per_run?, volume_source?, is_active?, notes? }
  saveLane: async (laneId, payload) => {
    const res = await api.put(`/secondary/lanes/${laneId}`, payload);
    return res.data;
  },

  deleteLane: async (laneId) => {
    const res = await api.delete(`/secondary/lanes/${laneId}`);
    return res.data;
  },

  // The page toggle. Stamps every lane in scope, so there is never a screen
  // where half the numbers mean something different from the other half.
  setVolumeSource: async (value, kind) => {
    const res = await api.put('/secondary/volume-source', { value, kind });
    return res.data;
  },

  // driver_code: 'DROPS' | 'VOLUME'. quantities: [12].
  // Overrides ONLY — a zero or a blank deletes rather than storing one, which
  // is what keeps "how much of this budget was typed" answerable.
  saveManualDrivers: async (laneId, driverCode, quantities) => {
    const res = await api.put(`/secondary/lanes/${laneId}/drivers`, {
      driver_code: driverCode, quantities,
    });
    return res.data;
  },

  // ---- Carriers on a lane --------------------------------------------------
  // Share is the CARRIER's slice of the lane, and defaults to whatever is left
  // of 100%. Rate components below it do not sum to anything.
  // payload: { lane_id, carrier_type?, contractor_code?, vehicle_type_code?,
  //            allocation_pct?, notes? }
  addCarrier: async (payload) => {
    const res = await api.post('/secondary/carriers', payload);
    return res.data;
  },

  saveCarrier: async (carrierId, payload) => {
    const res = await api.put(`/secondary/carriers/${carrierId}`, payload);
    return res.data;
  },

  deleteCarrier: async (carrierId) => {
    const res = await api.delete(`/secondary/carriers/${carrierId}`);
    return res.data;
  },

  // ---- Rate components -----------------------------------------------------
  // One carrier's quote has several: "R450 a drop PLUS R50 a pallet".
  // alloc_base defaults from the basis (per-run and per-km on drops, per-unit
  // on volume) — getting it wrong is invisible on the lane total and wrong on
  // every customer.
  // payload: { carrier_id, rate_basis?, alloc_base?, rate_mode?, rate_amount?, label? }
  addRate: async (payload) => {
    const res = await api.post('/secondary/rates', payload);
    return res.data;
  },

  // payload: { rate_basis?, alloc_base?, rate_mode?, label?, rates?: [12] }
  // Line and values in one call: the page edits them together, and splitting
  // it would let a basis change land without its rate.
  saveRate: async (rateLineId, payload) => {
    const res = await api.put(`/secondary/rates/${rateLineId}`, payload);
    return res.data;
  },

  deleteRate: async (rateLineId) => {
    const res = await api.delete(`/secondary/rates/${rateLineId}`);
    return res.data;
  },

  // ---- Posting (Budget Setup, admin to write) ------------------------------
  // Scoped site → zone → carrier type, each level optional, most specific wins.
  getPosting: async () => {
    const res = await api.get('/secondary/posting');
    return res.data;
  },

  savePosting: async (payload) => {
    const res = await api.put('/secondary/posting', payload);
    return res.data;
  },

  deletePosting: async (postingId) => {
    const res = await api.delete(`/secondary/posting/${postingId}`);
    return res.data;
  },
};

export default secondaryService;
